import React, { useEffect, useState } from "react";
import { IEpiProps, IFacet, IFacetsDisplayState, ISearchResultsContainer, ISelectedFilters } from "../models/vertex-search-page-models";
import { useVertexSearchContext, useVertexSearchDispatch } from "../reducers/vertex-search-page-provider";
import { SearchAnswerCitations } from "./search-answer-citations";
import { ShopFacet } from "./shop-facet";
import { Helpers } from "../helpers/helpers";

interface IProps {
    onShopFiltersChange: (shopFiltersString: string) => void;
}

interface IContextProps {
    epiProps: IEpiProps
    searchResultsContainer: ISearchResultsContainer
    selectedShopFilters: ISelectedFilters
    facetsShowMoreState: IFacetsDisplayState
    facetsAccordionClosedState: IFacetsDisplayState
}

export const ShopFacets = (props: IProps) => {
    const { epiProps, searchResultsContainer, selectedShopFilters, facetsShowMoreState, facetsAccordionClosedState }: IContextProps = useVertexSearchContext();
    const dispatch = useVertexSearchDispatch();
    const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>(selectedShopFilters);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Sync local state with context when selectedShopFilters changes
    useEffect(() => {
        setSelectedFilters(selectedShopFilters);
    }, [selectedShopFilters]);

    useEffect(() => {
        const handleResize = () => {
            const mobileBreakpoint = window.innerWidth <= 600;
            setIsMobile(mobileBreakpoint);
            if (!mobileBreakpoint) setFiltersOpen(true); // Ensure content is shown on desktop
        };

        handleResize(); // initial check
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    function getUpdatedSelectedFilters(
        prev: { [key: string]: string[] },
        facetKey: string,
        value: string,
        checked: boolean) {
        const prevValues = prev[facetKey] || [];
        let newValues;
        if (checked) {
            newValues = [...prevValues, value];
        } else {
            newValues = prevValues.filter(v => v !== value);
        }
        return { ...prev, [facetKey]: newValues };
    }

    function handleFilterChange(facetKey: string, value: string, checked: boolean) {
        let updatedFilters = getUpdatedSelectedFilters(selectedFilters, facetKey, value, checked);
        setSelectedFilters(updatedFilters);
        setSelectedShopFiltersInContext(updatedFilters);

        //Set selected SHOP filters in the query string
        var facetsQueryString = Helpers.createFacetsQueryString(updatedFilters);
        Helpers.setQueryParamToUrl("facets", facetsQueryString);

        let filtersString = Helpers.formatFiltersForGoogleVertexAPI(updatedFilters);
        setSelectedShopFiltersStringInContext(filtersString);
        props.onShopFiltersChange(filtersString);
    }

    function resetFilters() {
        setSelectedFilters({});
        setSelectedShopFiltersInContext({});
        setSelectedShopFiltersStringInContext("");
        setFacetsShowMoreStateInContext({});
        setFacetsAccordionClosedStateInContext({});
        props.onShopFiltersChange("");
    }

    function updateFacetsShowMoreStateInContext(facetKey: string) {
        var newFacetsShowMoreState = {
            ...facetsShowMoreState,
            [facetKey]: !facetsShowMoreState[facetKey]
        };
        setFacetsShowMoreStateInContext(newFacetsShowMoreState);
    }

    function updateFacetsAccordionClosedStateInContext(facetKey: string) {
        var newFacetsAccordionClosedState = {
            ...facetsAccordionClosedState,
            [facetKey]: !facetsAccordionClosedState[facetKey]
        };
        setFacetsAccordionClosedStateInContext(newFacetsAccordionClosedState);
    }

    function setSelectedShopFiltersInContext(selectedShopFilters: ISelectedFilters) {
        dispatch({
            type: 'updateSelectedShopFilters',
            data: selectedShopFilters
        })
    }

    function setSelectedShopFiltersStringInContext(selectedShopFiltersString: string) {
        dispatch({
            type: 'updateSelectedShopFiltersString',
            data: selectedShopFiltersString
        })
    }

    function setFacetsShowMoreStateInContext(facetsShowMoreState: IFacetsDisplayState) {
        dispatch({
            type: 'updateFacetsShowMoreState',
            data: facetsShowMoreState
        })
    }

    function setFacetsAccordionClosedStateInContext(facetsAccordionClosedState: IFacetsDisplayState) {
        dispatch({
            type: 'updateFacetsAccordionClosedState',
            data: facetsAccordionClosedState
        })
    }

    function showFacet(facet: IFacet): boolean {
        return facet != null && facet.values != null && facet.values.length > 0;
    }

    function getTotalSelectedFilters(): number {
        let totalSelectedFilters = 0;
        Object.values(selectedFilters).forEach(selectedFiltersInCategory => {
            totalSelectedFilters += selectedFiltersInCategory.length;
        });
        return totalSelectedFilters;
    }

    let showFacets = searchResultsContainer != null && searchResultsContainer.facets != null
        && searchResultsContainer.facets.length > 0 && !searchResultsContainer.facets.every(facet => !showFacet(facet));
    let totalSelectedFilters = getTotalSelectedFilters();
    return (
        <div className='search-facet'>
            <div className="search-facet-header">
                <div className="search-facet-header-inner">
                    <svg className="search-facet-header-icon">
                        <use xlinkHref="#icon-refine"></use>
                    </svg>
                    <button className="search-facet-mobile-toggle" type="button" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(prev => !prev)}>
                        {filtersOpen ?
                            <><svg className="open">
                                <use xlinkHref="#icon-close"></use>
                            </svg><span className="sr-only">Close Filters</span></> :
                            <><svg className="close">
                                <use xlinkHref="#icon-refine"></use>
                            </svg><span className="sr-only">Open Filters</span></>}
                        </button>
                    <h5>{epiProps.filterResultsHeading}</h5>
                </div>
                <div className="search-facet-header-actions">
                    <div className="search-facet-mobile-count"><span className="search-facet-mobile-count-text"><span className="sr-only">Filter Selected Count:</span>{totalSelectedFilters}</span></div>
                    <button className="search-facet-header-reset" onClick={resetFilters} disabled={!showFacets}>{epiProps.resetFiltersText}</button>
                </div>
            </div>
            <div hidden={isMobile && !filtersOpen}>
                {
                    showFacets && searchResultsContainer.facets.map((facet) => (
                        <ShopFacet
                            facet={facet}
                            showFacet={showFacet(facet)}
                            checkedValues={selectedFilters[facet.key] || []}
                            handleFilterChange={handleFilterChange}
                            updateFacetsShowMoreState={updateFacetsShowMoreStateInContext}
                            showAll={showFacet(facet) && !!facetsShowMoreState[facet.key]}
                            updateFacetsAccordionClosedStateInContext={updateFacetsAccordionClosedStateInContext}
                            accordionClosed={showFacet(facet) && !!facetsAccordionClosedState[facet.key]}
                        />
                    ))
                }
                {
                    !showFacets && <p>{epiProps.noFiltersMessage}</p>
                }
            </div>
        </div>
    )


}